<template>
  <div class="ExperienceStoreCard bg-bg-base border border-border-subtle p-16 flex flex-col gap-6">
    <div class="flex justify-between items-baseline gap-12">
      <span class="font-open font-semibold text-14 leading-20">{{ store.name }}</span>
      <span class="font-open text-12 leading-16 text-text-subtle whitespace-nowrap">{{ distance }}</span>
    </div>
    <div class="font-open text-12 leading-16 text-text-subtle">{{ address }}</div>
    <div class="font-open font-semibold text-12 leading-16 text-text-attention flex items-center gap-8">
      <GlobalIcon
        name="check"
        size="xsmall"
        color="attention"
        class="shrink-0 !w-12 !h-12"
      />
      {{ $t('pdp.experience.results.hasDisplay') }}
    </div>

    <button
      type="button"
      class="font-open font-semibold text-12 leading-16 underline flex items-center gap-6 w-fit py-4"
      :aria-expanded="open"
      :aria-controls="detailsId"
      @click="open = !open"
    >
      {{ $t('pdp.experience.results.directions') }}
      <GlobalIcon
        :name="open ? 'chevron-up' : 'chevron-down'"
        size="xsmall"
        color="base"
        class="shrink-0 !w-12 !h-12"
      />
    </button>

    <div
      v-if="open"
      :id="detailsId"
      class="flex flex-col gap-8 pt-8 border-t border-border-subtle font-open text-12 leading-16"
    >
      <div class="relative w-full h-144 overflow-hidden">
        <GoogleMap
          v-slot="{ ready }"
          :api-key="runtimeConfig.public.googleMapsApiKey"
          style="width: 100%; height: 100%"
          :styles="mapStyles"
          :center="{ lat: store.lat, lng: store.lng }"
          :zoom="15"
          :disable-default-ui="true"
          gesture-handling="none"
        >
          <Marker
            v-if="ready"
            :options="{ position: { lat: store.lat, lng: store.lng }, icon: markerIcon() }"
          />
        </GoogleMap>
        <a
          :href="directionsUrl"
          target="_blank"
          rel="noopener"
          class="absolute left-12 bottom-12 flex items-center gap-8 px-12 py-8 bg-bg-base border border-border-base font-semibold"
        >
          {{ $t('pdp.experience.results.openMaps') }}
          <img
            src="/icons/external-link-inv.svg"
            class="w-16 h-16 invert"
            alt=""
          >
        </a>
      </div>
      <dl class="grid grid-cols-[auto_1fr] gap-x-16 gap-y-4">
        <dt class="text-text-subtle">{{ $t('form.placeholder.street') }}</dt>
        <dd>{{ address }}</dd>
        <template v-if="store.phone">
          <dt class="text-text-subtle">{{ $t('retailer.contact.phoneTitle') }}</dt>
          <dd><a :href="`tel:${store.phone.replaceAll(' ', '')}`" class="underline">{{ store.phone }}</a></dd>
        </template>
        <template v-if="store.email">
          <dt class="text-text-subtle">{{ $t('retailer.contact.emailTitle') }}</dt>
          <dd><a :href="`mailto:${store.email}`" class="underline break-all">{{ store.email }}</a></dd>
        </template>
        <template v-if="websiteUrl">
          <dt class="text-text-subtle">{{ $t('retailer.contact.websiteTitle') }}</dt>
          <dd>
            <a
              :href="websiteUrl"
              target="_blank"
              rel="noopener"
              class="underline break-all"
            >{{ websiteLabel }}</a>
          </dd>
        </template>
      </dl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { GoogleMap, Marker } from 'vue3-google-map';
import type { DisplayStore } from '~/util/displayStores';
import { useGlobalContentStore } from '~/store/globalContent';

const props = defineProps<{
  store: DisplayStore,
}>();

const globalContent = useGlobalContentStore();
const runtimeConfig = useRuntimeConfig();
const open = ref(false);

const mapStyles = [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }];

const markerIcon = () => ({
  url: '/logos/kahrs-logo-round.svg',
  scaledSize: new google.maps.Size(32, 32),
  anchor: new google.maps.Point(16, 16),
});
const detailsId = `storeDetails-${props.store.accountid}`;

const distance = computed(() => `${new Intl.NumberFormat(globalContent.getLocale, { maximumFractionDigits: 1 }).format(props.store.distanceKm)} km`);
const address = computed(() => [props.store.addressLine1, [props.store.postalcode, props.store.city].filter(Boolean).join(' ')].filter(Boolean).join(', '));
const directionsUrl = computed(() => `https://www.google.com/maps/dir/?api=1&destination=${props.store.lat},${props.store.lng}`);
const websiteUrl = computed(() => {
  const site = props.store.website?.trim();
  if (!site) {
    return '';
  }
  return /^https?:\/\//i.test(site) ? site : `https://${site}`;
});
const websiteLabel = computed(() => websiteUrl.value.replace(/^https?:\/\//i, '').replace(/\/$/, ''));
</script>
